/**
 * Route Search Bar Component
 * 
 * Reusable search input component for filtering routes by name or ID.
 * Used by both AtlasRoutesPanel and DatabaseRoutesPanel.
 * 
 * @module RouteSearchBar
 */

import React from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
} from '@mui/material';

import { useTranslation } from 'react-i18next';

import { Icon } from '@liftango/ui-icons';
/**
 * Props for RouteSearchBar component
 */
export interface RouteSearchBarProps {
  /** Current search query */
  searchQuery: string;
  /** Callback when search query changes */
  onSearchChange: (query: string) => void;
  /** Placeholder text for the search input */
  placeholder?: string;
}

/**
 * RouteSearchBar Component
 * 
 * Search input with clear button for filtering routes.
 */
export const RouteSearchBar: React.FC<RouteSearchBarProps> = ({
  searchQuery,
  onSearchChange,
  placeholder,
}) => {
  const { t } = useTranslation('mapTransfer');
  const resolvedPlaceholder = placeholder ?? t('routes.panel.searchPlaceholderAtlas');
  return (
    <Box sx={{ marginBottom: 2 }}>
      <TextField
        fullWidth
        size="small"
        placeholder={resolvedPlaceholder}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon name="magnifying-glass" size={20} />
            </InputAdornment>
          ),
          endAdornment: searchQuery && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => onSearchChange('')}
                edge="end"
                aria-label={t('common.clearSearch')}
              >
                <Icon name="xmark" size={20} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};

